import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../supabaseSync';
import { getTypedSupabaseClient } from '../typedSupabaseClient';
import { autoRecordDecision } from '../decisionService';
import { actRightFromError, readActRight, type ActRight } from '../actRight';

export interface ApprovePriorityListArgs {
  actorId: string;
  itemIds: string[];
  cycleId?: string | null;
  loopId?: string | null;
  note?: string | null;
}

export interface ApprovePriorityListResult {
  success: boolean;
  approvedCount: number;
  approvedAt: string;
  approvedBy: string;
  governanceEventId: string;
  source: 'catchball' | 'item_pool';
}

const APPROVE_FALLBACK = 'You may not approve items onto the Priority List.';

/**
 * May the signed-in person approve items onto this Priority List?
 * `can_approve_priority_list` (migrations 1001, 1017) answers with the rule
 * `approve_priority_list` acts on: the cycle owner, or a holder of the right to
 * publish lists under either of its names (a workspace admin among them), and
 * then the cycle's "Items / selection" decision right when one is set — the same
 * right ratifying a list asks. A list with no cycle names its workspace instead.
 * A page draws Approve only when `allowed`.
 */
export async function fetchApproveRight(
  cycleId: string | null,
  loopId?: string | null,
  workspaceId?: string | null,
): Promise<ActRight> {
  try {
    const { data, error } = await getTypedSupabaseClient().rpc('can_approve_priority_list', {
      p_cycle_id: cycleId ?? undefined,
      p_loop_id: loopId ?? undefined,
      ...(cycleId ? {} : { p_workspace_id: workspaceId ?? undefined }),
    });
    if (error) return actRightFromError(error, 'approve items onto the Priority List');
    return readActRight(data, APPROVE_FALLBACK);
  } catch (err) {
    return actRightFromError({ message: err instanceof Error ? err.message : String(err) }, 'approve items onto the Priority List');
  }
}

/**
 * Approve items onto the Priority List via the approve_priority_list RPC.
 *
 * Authority transition (Build Contract §2.3a):
 * - Atomically sets leadership_approved = true on each item
 * - Appends a governance_events audit row
 * - Returns authoritative state for UI rendering
 *
 * Permission: priority_list.approve (registry-decision-rights.yaml)
 * Callers draw the control from `fetchApproveRight` and pass the cycle the list
 * belongs to, so the act judges what the question judged (migration 1017).
 */
export async function approvePriorityList(
  args: ApprovePriorityListArgs
): Promise<ApprovePriorityListResult> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    throw new Error('Supabase client not available');
  }

  const { data, error } = await supabase.rpc('approve_priority_list', {
    p_actor_id: args.actorId,
    p_item_ids: args.itemIds,
    p_cycle_id: args.cycleId ?? null,
    p_loop_id: args.loopId ?? null,
    p_note: args.note ?? null,
  });

  if (error) {
    throw new Error(`approvePriorityList failed: ${error.message}`);
  }

  if (!data?.success) {
    throw new Error(data?.error ?? 'approvePriorityList returned failure');
  }

  const result = data as ApprovePriorityListResult;

  // Auto-record decision: priority list approved → force_ranking decision (spec §3.3)
  autoRecordDecision({
    source: 'priority_list_approved',
    workspaceId: getCurrentWorkspaceId() ?? '',
    actorId: args.actorId,
    sourceEventId: result.governanceEventId ?? null,
    cycleId: args.cycleId ?? null,
    itemCount: args.itemIds.length,
    relatedObjects: [
      ...(args.cycleId ? [{ kind: 'cycle' as const, id: args.cycleId }] : []),
    ],
  }).catch(() => {});

  return result;
}
